import { useMemo, useState, useEffect, useRef } from 'react';
import { View, Pressable, Modal, ScrollView, Alert, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { formatCurrency } from '@/utils/currency';
import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import type { UserProfile } from '@/state/userStore';
import {
  shouldShowTripCostConfidence,
  type TripCostConfidence,
  type TripCostStyle,
} from '@/services/tripCostConfidence';
import { useTripCostConfidence } from '@/hooks/useTripCostConfidence';
import { usePlanningIntelligencePremium } from '@/hooks/usePlanningIntelligencePremium';
import { logAnalyticsEvent } from '@/services/analyticsLogger';

type TripCostConfidenceSheetProps = {
  visible: boolean;
  onClose: () => void;
  confidence: TripCostConfidence;
  style: TripCostStyle;
  bufferPct: number;
  onStyleChange: (style: TripCostStyle) => void;
  onBufferChange: (pct: number) => void;
  onApplyBudget?: (amount: number) => void;
  onOpenCustomBudget?: () => void;
  travelType?: UserProfile['travelType'];
};

const STYLES: TripCostStyle[] = ['budget', 'comfort', 'premium'];
const BUFFER_OPTIONS = [8, 12, 15, 20];

const confidenceMeta = (
  level: TripCostConfidence['confidence'],
  theme: ReturnType<typeof useTheme>,
): { shortLabel: string; label: string; color: string; hint: string } => {
  if (level === 'high') {
    return {
      shortLabel: 'High',
      label: 'High confidence',
      color: theme.colors.success || '#22C55E',
      hint: 'Mostly based on synced bookings',
    };
  }
  if (level === 'medium') {
    return {
      shortLabel: 'Medium',
      label: 'Medium confidence',
      color: theme.colors.warning || '#F59E0B',
      hint: 'Some bookings or budget on file',
    };
  }
  return {
    shortLabel: 'Low',
    label: 'Low confidence',
    color: theme.colors.error || '#EF4444',
    hint: 'Rough estimate — add bookings to improve',
  };
};

export function TripCostConfidenceSheet({
  visible,
  onClose,
  confidence,
  style,
  bufferPct,
  onStyleChange,
  onBufferChange,
  onApplyBudget,
  onOpenCustomBudget,
}: TripCostConfidenceSheetProps) {
  const theme = useTheme();

  const promptSetBudget = () => {
    if (!onApplyBudget && !onOpenCustomBudget) return;
    const mid = Math.round((confidence.rangeMin + confidence.rangeMax) / 2);
    Alert.alert(
      'Set trip budget',
      `Choose an amount from your estimate (${formatCurrency(confidence.rangeMin, confidence.currency)} – ${formatCurrency(confidence.rangeMax, confidence.currency)}).`,
      [
        onApplyBudget
          ? {
              text: `Lower (${formatCurrency(confidence.rangeMin, confidence.currency)})`,
              onPress: () => onApplyBudget(confidence.rangeMin),
            }
          : undefined,
        onApplyBudget
          ? {
              text: `Midpoint (${formatCurrency(mid, confidence.currency)})`,
              onPress: () => onApplyBudget(mid),
            }
          : undefined,
        onApplyBudget
          ? {
              text: `Upper (${formatCurrency(confidence.rangeMax, confidence.currency)})`,
              onPress: () => onApplyBudget(confidence.rangeMax),
            }
          : undefined,
        onOpenCustomBudget
          ? { text: 'Custom amount…', onPress: onOpenCustomBudget }
          : undefined,
        { text: 'Cancel', style: 'cancel' },
      ].filter(Boolean) as Parameters<typeof Alert.alert>[2],
    );
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: theme.colors.background, padding: theme.spacing(2) }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(2) }}>
          <SubHeading>Cost breakdown</SubHeading>
          <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel="Close">
            <Ionicons name="close" size={24} color={theme.colors.text} />
          </Pressable>
        </View>

        <ScrollView contentContainerStyle={{ gap: theme.spacing(2), paddingBottom: theme.spacing(4) }}>
          <Card variant="outlined">
            <ThemedText variant="hero" style={{ fontSize: 20, fontWeight: '700' }}>
              {formatCurrency(confidence.rangeMin, confidence.currency)} –{' '}
              {formatCurrency(confidence.rangeMax, confidence.currency)}
            </ThemedText>
            <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
              Includes {confidence.bufferPct}% buffer · {confidence.nights} nights · {confidence.partySize} traveler
              {confidence.partySize > 1 ? 's' : ''}
            </Caption>
          </Card>

          <Card variant="outlined">
            <SubHeading style={{ fontSize: 15, marginBottom: theme.spacing(1) }}>Trip style</SubHeading>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
              {STYLES.map((s) => (
                <Pressable
                  key={s}
                  onPress={() => onStyleChange(s)}
                  style={{
                    paddingHorizontal: theme.spacing(1.25),
                    paddingVertical: theme.spacing(0.75),
                    borderRadius: theme.radii.pill,
                    borderWidth: 1,
                    borderColor: style === s ? theme.colors.primary : theme.colors.borderMuted,
                    backgroundColor: style === s ? theme.colors.primary + '18' : theme.colors.surface,
                  }}
                >
                  <Caption style={{ fontWeight: '700', textTransform: 'capitalize' }}>{s}</Caption>
                </Pressable>
              ))}
            </View>
          </Card>

          <Card variant="outlined">
            <SubHeading style={{ fontSize: 15, marginBottom: theme.spacing(1) }}>Buffer</SubHeading>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
              {BUFFER_OPTIONS.map((pct) => (
                <Pressable
                  key={pct}
                  onPress={() => onBufferChange(pct)}
                  style={{
                    paddingHorizontal: theme.spacing(1.25),
                    paddingVertical: theme.spacing(0.75),
                    borderRadius: theme.radii.pill,
                    borderWidth: 1,
                    borderColor: bufferPct === pct ? theme.colors.primary : theme.colors.borderMuted,
                    backgroundColor: bufferPct === pct ? theme.colors.primary + '18' : theme.colors.surface,
                  }}
                >
                  <Caption style={{ fontWeight: '700' }}>{pct}%</Caption>
                </Pressable>
              ))}
            </View>
          </Card>

          <Card variant="outlined">
            <SubHeading style={{ fontSize: 15, marginBottom: theme.spacing(1) }}>By category</SubHeading>
            {confidence.breakdown.map((row) => (
              <View
                key={row.category}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  paddingVertical: theme.spacing(0.75),
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.borderMuted,
                }}
              >
                <Body>{row.category}</Body>
                <Caption style={{ fontWeight: '600' }}>
                  {formatCurrency(row.min, confidence.currency)} – {formatCurrency(row.max, confidence.currency)}
                </Caption>
              </View>
            ))}
          </Card>

          <Card variant="outlined">
            <SubHeading style={{ fontSize: 15, marginBottom: theme.spacing(1) }}>Why this range</SubHeading>
            {confidence.reasons.map((reason) => (
              <View key={reason} style={{ flexDirection: 'row', gap: theme.spacing(0.75), marginBottom: theme.spacing(0.5) }}>
                <Ionicons name="checkmark-circle-outline" size={16} color={theme.colors.primary} />
                <Body color={theme.colors.textSecondary} style={{ flex: 1, fontSize: 13 }}>
                  {reason}
                </Body>
              </View>
            ))}
          </Card>

          {onApplyBudget || onOpenCustomBudget ? (
            <Button
              label="Set trip budget"
              onPress={promptSetBudget}
              icon={<Ionicons name="wallet-outline" size={18} color={theme.colors.surface} />}
            />
          ) : null}
        </ScrollView>
      </View>
    </Modal>
  );
}

type TripCostConfidenceCardProps = {
  trip: Trip;
  bookings?: Booking[];
  profile?: Pick<UserProfile, 'travelType'>;
  isArchived?: boolean;
  dismissed?: boolean;
  onDismiss?: () => void;
  onApplyBudget?: (amount: number) => void;
  onOpenCustomBudget?: () => void;
  style?: TripCostStyle;
  bufferPct?: number;
  partySize?: number;
  onStyleChange?: (style: TripCostStyle) => void;
  onBufferChange?: (pct: number) => void;
  /** @deprecated use onApplyBudget */
  onOpenWallet?: () => void;
};

export function TripCostConfidenceCard({
  trip,
  bookings = [],
  profile,
  isArchived = false,
  dismissed = false,
  onDismiss,
  onApplyBudget,
  onOpenCustomBudget,
  onOpenWallet,
  style: styleProp = 'comfort',
  bufferPct: bufferProp = 12,
  partySize = 1,
  onStyleChange,
  onBufferChange,
}: TripCostConfidenceCardProps) {
  const theme = useTheme();
  const [sheetOpen, setSheetOpen] = useState(false);
  const viewedRef = useRef(false);
  const { isPremium, requirePremium } = usePlanningIntelligencePremium();

  const { confidence, aiLoading, aiError, isAiPowered } = useTripCostConfidence({
    trip,
    bookings,
    style: styleProp,
    partySize,
    bufferPct: bufferProp,
    isPremium,
    enabled: isPremium && !dismissed && shouldShowTripCostConfidence(trip, isArchived),
  });

  const meta = confidenceMeta(confidence.confidence, theme);

  useEffect(() => {
    if (viewedRef.current || dismissed || !shouldShowTripCostConfidence(trip, isArchived)) return;
    viewedRef.current = true;
    void logAnalyticsEvent('trip_cost_confidence_viewed', {
      tripId: trip.id,
      confidence: confidence.confidence,
      rangeMin: confidence.rangeMin,
      rangeMax: confidence.rangeMax,
    });
  }, [trip, isArchived, dismissed, confidence.confidence, confidence.rangeMin, confidence.rangeMax]);

  const handleSetBudget = () => {
    if (!requirePremium('trip_cost_confidence')) return;
    if (onApplyBudget || onOpenCustomBudget) {
      const mid = Math.round((confidence.rangeMin + confidence.rangeMax) / 2);
      Alert.alert(
        'Set trip budget',
        `Choose an amount from your estimate (${formatCurrency(confidence.rangeMin, confidence.currency)} – ${formatCurrency(confidence.rangeMax, confidence.currency)}).`,
        [
          onApplyBudget
            ? {
                text: `Lower (${formatCurrency(confidence.rangeMin, confidence.currency)})`,
                onPress: () => {
                  void logAnalyticsEvent('trip_cost_confidence_budget_linked', {
                    tripId: trip.id,
                    source: 'lower',
                    amount: confidence.rangeMin,
                  });
                  onApplyBudget(confidence.rangeMin);
                  setSheetOpen(false);
                },
              }
            : undefined,
          onApplyBudget
            ? {
                text: `Midpoint (${formatCurrency(mid, confidence.currency)})`,
                onPress: () => {
                  void logAnalyticsEvent('trip_cost_confidence_budget_linked', {
                    tripId: trip.id,
                    source: 'mid',
                    amount: mid,
                  });
                  onApplyBudget(mid);
                  setSheetOpen(false);
                },
              }
            : undefined,
          onApplyBudget
            ? {
                text: `Upper (${formatCurrency(confidence.rangeMax, confidence.currency)})`,
                onPress: () => {
                  void logAnalyticsEvent('trip_cost_confidence_budget_linked', {
                    tripId: trip.id,
                    source: 'upper',
                    amount: confidence.rangeMax,
                  });
                  onApplyBudget(confidence.rangeMax);
                  setSheetOpen(false);
                },
              }
            : undefined,
          onOpenCustomBudget
            ? { text: 'Custom amount…', onPress: onOpenCustomBudget }
            : onOpenWallet
              ? { text: 'Open Wallet…', onPress: onOpenWallet }
              : undefined,
          { text: 'Cancel', style: 'cancel' },
        ].filter(Boolean) as Parameters<typeof Alert.alert>[2],
      );
      return;
    }
    onOpenWallet?.();
  };

  if (!shouldShowTripCostConfidence(trip, isArchived) || dismissed) {
    return null;
  }

  if (!isPremium) {
    return (
      <Card variant="outlined" style={{ gap: theme.spacing(1), marginBottom: theme.spacing(2) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
          <Ionicons name="wallet-outline" size={20} color={theme.colors.primary} />
          <SubHeading style={{ fontSize: 16, flex: 1 }}>Trip cost confidence</SubHeading>
        </View>
        <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
          Premium unlocks AI-powered cost ranges based on your route, dates, and travel style — with one-tap budget setup.
        </Body>
        <Button
          label="Unlock with Premium"
          variant="secondary"
          onPress={() => requirePremium('trip_cost_confidence')}
        />
      </Card>
    );
  }

  return (
    <>
      <Card variant="outlined" style={{ gap: theme.spacing(1.25), marginBottom: theme.spacing(2) }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flex: 1, minWidth: 0 }}>
            <Ionicons name="wallet-outline" size={20} color={theme.colors.primary} />
            <SubHeading style={{ fontSize: 16, flexShrink: 1 }} numberOfLines={2}>
              Trip cost confidence
            </SubHeading>
          </View>
          {onDismiss ? (
            <Pressable
              onPress={onDismiss}
              accessibilityRole="button"
              accessibilityLabel="Dismiss cost estimate"
              hitSlop={8}
              style={{ padding: theme.spacing(0.35), marginTop: 2 }}
            >
              <Ionicons name="close" size={18} color={theme.colors.textSecondary} />
            </Pressable>
          ) : null}
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flexWrap: 'wrap' }}>
          <View
            style={{
              paddingHorizontal: theme.spacing(1),
              paddingVertical: theme.spacing(0.35),
              borderRadius: theme.radii.pill,
              backgroundColor: meta.color + '22',
              borderWidth: 1,
              borderColor: meta.color + '66',
            }}
          >
            <Caption style={{ fontWeight: '700', color: meta.color }}>{meta.shortLabel} confidence</Caption>
          </View>
          {aiLoading ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <ActivityIndicator size="small" color={theme.colors.primary} />
              <Caption color={theme.colors.textSecondary}>Updating estimate…</Caption>
            </View>
          ) : isAiPowered ? (
            <Caption color={theme.colors.textSecondary} style={{ fontWeight: '600' }}>
              AI estimate
            </Caption>
          ) : null}
        </View>

        <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
          {meta.hint}. Estimated total before you finalize bookings.
        </Caption>
        {aiError ? (
          <Caption color={theme.colors.warning} style={{ fontSize: 11 }}>
            Using fallback estimate — {aiError}
          </Caption>
        ) : null}

        <ThemedText variant="hero" style={{ fontSize: 22, fontWeight: '700' }}>
          {formatCurrency(confidence.rangeMin, confidence.currency)} –{' '}
          {formatCurrency(confidence.rangeMax, confidence.currency)}
        </ThemedText>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
          {[
            { label: 'Transport', value: confidence.drivers.transport },
            { label: 'Stay', value: confidence.drivers.stay },
            { label: 'In-trip', value: confidence.drivers.inDestination },
          ].map(({ label, value }) => (
            <View
              key={label}
              style={{
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.pill,
                backgroundColor: theme.colors.borderMuted + '40',
              }}
            >
              <Caption style={{ fontWeight: '600' }}>
                {label}: {formatCurrency(value, confidence.currency)}
              </Caption>
            </View>
          ))}
        </View>

        <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
          <Button
            label="See breakdown"
            variant="secondary"
            onPress={() => {
              if (!requirePremium('trip_cost_confidence')) return;
              setSheetOpen(true);
            }}
            style={{ flex: 1 }}
          />
          {(onApplyBudget || onOpenCustomBudget || onOpenWallet) ? (
            <Button label="Set budget" variant="secondary" onPress={handleSetBudget} style={{ flex: 1 }} />
          ) : null}
        </View>
      </Card>

      <TripCostConfidenceSheet
        visible={sheetOpen}
        onClose={() => setSheetOpen(false)}
        confidence={confidence}
        style={styleProp}
        bufferPct={bufferProp}
        onStyleChange={(next) => {
          onStyleChange?.(next);
          void logAnalyticsEvent('trip_cost_confidence_adjusted', { tripId: trip.id, style: next });
        }}
        onBufferChange={(next) => {
          onBufferChange?.(next);
          void logAnalyticsEvent('trip_cost_confidence_adjusted', { tripId: trip.id, bufferPct: next });
        }}
        onApplyBudget={
          onApplyBudget
            ? (amount) => {
                void logAnalyticsEvent('trip_cost_confidence_budget_linked', { tripId: trip.id, source: 'sheet', amount });
                setSheetOpen(false);
                onApplyBudget(amount);
              }
            : undefined
        }
        onOpenCustomBudget={
          onOpenCustomBudget
            ? () => {
                setSheetOpen(false);
                onOpenCustomBudget();
              }
            : onOpenWallet
              ? () => {
                  setSheetOpen(false);
                  onOpenWallet();
                }
              : undefined
        }
        travelType={profile?.travelType}
      />
    </>
  );
}
